export const MAX_SUBDOMAIN_LENGTH = 63;
export const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024;

export type IntakeValidationResult = {
  isValid: boolean;
  errors: Record<string, string>;
  sanitizedSubdomain: string;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const hexColorPattern = /^#[0-9a-f]{6}$/i;

export function sanitizeSubdomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, MAX_SUBDOMAIN_LENGTH)
    .replace(/-$/g, "");
}

export function isValidSubdomain(value: string): boolean {
  if (!value || value.length > MAX_SUBDOMAIN_LENGTH) {
    return false;
  }

  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(value);
}

export function isValidHexColor(value: string): boolean {
  return hexColorPattern.test(value);
}

export function validateIntakeForm(formData: FormData): IntakeValidationResult {
  const errors: Record<string, string> = {};
  const brokerEmail = String(formData.get("broker_email") ?? "").trim();
  const clientCompanyName = String(formData.get("client_company_name") ?? "").trim();
  const requestedSubdomain = String(formData.get("requested_subdomain") ?? "");
  const sanitizedSubdomain = sanitizeSubdomain(requestedSubdomain);
  const logo = formData.get("company_logo");
  const colors = [
    String(formData.get("brand_color_1") ?? ""),
    String(formData.get("brand_color_2") ?? ""),
    String(formData.get("brand_color_3") ?? ""),
    String(formData.get("brand_color_4") ?? "")
  ];

  if (!emailPattern.test(brokerEmail)) {
    errors.broker_email = "Enter a valid broker email address.";
  }

  if (!clientCompanyName) {
    errors.client_company_name = "Enter the client's formal company name.";
  }

  if (!isValidSubdomain(sanitizedSubdomain)) {
    errors.requested_subdomain = "Enter a name that can become a valid subdomain.";
  }

  if (!(logo instanceof File) || logo.size === 0) {
    errors.company_logo = "Upload a PNG or JPG company logo.";
  } else if (!["image/png", "image/jpeg"].includes(logo.type)) {
    errors.company_logo = "Logo must be a PNG or JPG file.";
  } else if (logo.size > MAX_LOGO_SIZE_BYTES) {
    errors.company_logo = "Logo must be 5 MB or smaller.";
  }

  if (!colors.every(isValidHexColor)) {
    errors.brand_colors = "Select four valid brand colors.";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    sanitizedSubdomain
  };
}
